// DOM for one value by its shape: plain (lists, conditionals), slot cells, step bar
// with its sections, linear profile with its points, raw expands. `doc` is injected
// (the page's document, or the fake one in tests); text goes in with textContent only.
import { baseKey } from "./lrValues.mjs";
import {
  axisEndCm,
  buildProfile,
  closedStepSections,
  isProfilePoint,
} from "./positions.mjs";
import { isSlotKey } from "./slots.mjs";

const SVG_NS = "http://www.w3.org/2000/svg";
const SHADES = 8;
// A segment or band gets a text label only when it is this wide.
const LABEL_MIN_PERCENT = 12;

export function el(doc, tagName, className, text) {
  const node = doc.createElement(tagName);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = String(text);
  return node;
}

function svgEl(doc, tagName, attributes) {
  const node = doc.createElementNS(SVG_NS, tagName);
  Object.entries(attributes).forEach(([k, v]) => node.setAttribute(k, v));
  return node;
}

/** @returns {string} a value as one line of text, as tagged */
export function valueText(value) {
  if (value === null) return "null";
  if (value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const percent = (cm, axis) => `${(cm / axis) * 100}%`;

/** `;` outside parentheses: a conditional's window may hold `;` between its rules. */
function splitList(text) {
  const parts = [];
  let depth = 0;
  let current = "";
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === "\\" && text[i + 1] === ";") {
      current += ";";
      i++;
      continue;
    }
    if (char === "(") depth++;
    if (char === ")") depth = Math.max(0, depth - 1);
    if (char === ";" && depth === 0) {
      parts.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  parts.push(current.trim());
  return parts;
}

const CONDITIONAL = /^(.*?)\s*@\s*\((.*)\)\s*$/s;

function appendItem(doc, parent, text) {
  const match = CONDITIONAL.exec(text);
  if (!match || match[1] === "") {
    parent.append(text);
    return;
  }
  parent.append(match[1]);
  parent.append(el(doc, "div", "fe-when", `@ (${match[2]})`));
}

/**
 * A whole-way value: `;` list one item per line, `value @ (window)` with the window
 * on its own line, null as `null`, objects as a nested table.
 */
export function renderPlainValue(doc, value) {
  if (value !== null && typeof value === "object") {
    const table = el(
      doc,
      "table",
      "ol-selectable vector-feature-subattributes-list",
    );
    Object.entries(value).forEach(([key, sub]) => {
      const tr = el(doc, "tr");
      tr.append(el(doc, "td", "label", key));
      const td = el(doc, "td", "value");
      td.append(renderPlainValue(doc, sub));
      tr.append(td);
      table.append(tr);
    });
    return table;
  }
  const text = valueText(value);
  const items = typeof value === "string" ? splitList(text) : [text];
  if (items.length === 1 && !CONDITIONAL.test(text)) {
    return el(doc, "span", "fe-value", text);
  }
  const node = el(doc, "div", "fe-value fe-list");
  items.forEach((item) => {
    const line = el(doc, "div", "fe-item");
    appendItem(doc, line, item);
    node.append(line);
  });
  return node;
}

/** One cell per `|` slot; a blank slot shows `·`. */
export function renderSlotCells(doc, value) {
  const node = el(doc, "div", "fe-slots");
  String(value)
    .split("|")
    .forEach((slot, i) => {
      const text = slot.trim();
      const cell = el(doc, "span", "fe-slot");
      cell.dataset.tip = `slot ${i + 1}: ${text === "" ? "(blank)" : text}`;
      cell.append(text === "" ? el(doc, "span", "fe-blank", "·") : text);
      node.append(cell);
    });
  return node;
}

function shadeIndex(values) {
  const distinct = [...new Set(values)];
  return (value) => distinct.indexOf(value) % SHADES;
}

function sectionEndText(sections, i, lengthCm) {
  const s = sections[i];
  if (s.endCm !== null) return s.endCm;
  const next = sections.slice(i + 1).find((n) => n.startCm > s.startCm);
  if (next) return next.startCm;
  return lengthCm ?? "end";
}

/** A step tag: a thin bar along the way, then one line per section (unless `list` is false). */
export function renderStepBlock(
  doc,
  tag,
  lengthCm,
  { list: withList = true } = {},
) {
  const node = el(doc, "div", "fe-step");
  const axis = axisEndCm(lengthCm, tag.sections);
  const bar = el(doc, "div", "fe-bar");
  bar.dataset.axis = String(axis);
  const closed = closedStepSections(tag.sections, lengthCm);
  const shade = shadeIndex(closed.map((s) => s.value));
  closed.forEach((s) => {
    const seg = el(doc, "span", `fe-seg fe-shade-${shade(s.value)}`);
    seg.style.left = percent(s.startCm, axis);
    seg.style.width = percent(s.endCm - s.startCm, axis);
    seg.dataset.tip = s.value;
    seg.dataset.start = String(s.startCm);
    seg.dataset.end = String(s.endCm);
    if (((s.endCm - s.startCm) / axis) * 100 > LABEL_MIN_PERCENT)
      seg.textContent = s.value;
    bar.append(seg);
  });
  node.append(bar);
  if (!withList) return node;

  const slots = isSlotKey(tag.key);
  const list = el(doc, "table", "fe-sections");
  tag.sections.forEach((s, i) => {
    const tr = el(doc, "tr", "fe-sec");
    tr.dataset.index = String(i);
    tr.append(
      el(
        doc,
        "td",
        "fe-range",
        `${s.startCm}–${sectionEndText(tag.sections, i, lengthCm)}`,
      ),
    );
    const td = el(doc, "td");
    td.append(
      slots ? renderSlotCells(doc, s.value) : renderPlainValue(doc, s.value),
    );
    tr.append(td);
    list.append(tr);
  });
  node.append(list);
  return node;
}

/** A spot tag: one line per offset. */
export function renderSpotList(doc, tag) {
  const list = el(doc, "table", "fe-sections");
  tag.sections.forEach((s, i) => {
    const tr = el(doc, "tr", "fe-sec");
    tr.dataset.index = String(i);
    tr.append(el(doc, "td", "fe-range", `at ${s.startCm}`));
    const td = el(doc, "td");
    td.append(renderPlainValue(doc, s.value));
    tr.append(td);
    list.append(tr);
  });
  return list;
}

export const isCurvatureKey = (key) => {
  const base = baseKey(key);
  return base === "curvature" || base === "meta_full_curvature";
};

function profileHint(profile) {
  if (!profile.bands.length) return "";
  return `hatched: ${[...new Set(profile.bands.map((b) => b.value))].join(", ")}`;
}

/** A linear tag: profile with a y axis (max, 0, min), bands hatched, then every point (unless `points` is false). */
export function renderProfile(
  doc,
  tag,
  lengthCm,
  { points: withPoints = true } = {},
) {
  const profile = buildProfile(tag.sections, lengthCm);
  const axis = profile.axisEndCm;
  const W = 400;
  const H = 56;
  const span = profile.max - profile.min || 1;
  const x = (cm) => (cm / axis) * W;
  const y = (v) => 3 + (1 - (v - profile.min) / span) * (H - 6);

  const node = el(doc, "div", "fe-prof");
  const grid = el(doc, "div", "fe-profgrid");
  const ylab = el(doc, "div", "fe-ylab");
  const ticks = [...new Set([profile.max, 0, profile.min])];
  // Gridlines at every tick; a label only where it does not overlap one already
  // placed (0 first, so a min of -7 under a max of 126 does not print over it).
  const placed = [];
  [0, profile.max, profile.min].forEach((v) => {
    const top = y(v);
    if (placed.some((p) => Math.abs(p - top) < 10)) return;
    placed.push(top);
    const label = el(doc, "span", undefined, v);
    label.style.top = `${(top / H) * 100}%`;
    ylab.append(label);
  });
  const plot = el(doc, "div", "fe-profile");
  plot.dataset.axis = String(axis);
  // The pointer tooltip re-reads the chain to give the value under the pointer.
  plot.dataset.chain = valueText(tag.value);
  plot.dataset.length =
    lengthCm === null || lengthCm === undefined ? "" : String(lengthCm);
  const svg = svgEl(doc, "svg", {
    viewBox: `0 0 ${W} ${H}`,
    preserveAspectRatio: "none",
  });
  ticks.forEach((v) =>
    svg.append(
      svgEl(doc, "line", {
        x1: 0,
        x2: W,
        y1: y(v),
        y2: y(v),
        class: v === 0 ? "fe-zero" : "fe-grid",
      }),
    ),
  );
  // A band (`multiple`, or a ranged section) breaks the line.
  const runs = [[]];
  tag.sections.forEach((s) => {
    if (isProfilePoint(s))
      runs[runs.length - 1].push({ cm: s.startCm, value: Number(s.value) });
    else runs.push([]);
  });
  runs
    .filter((run) => run.length > 0)
    .forEach((run) =>
      svg.append(
        svgEl(doc, "polyline", {
          points: run.map((p) => `${x(p.cm)},${y(p.value)}`).join(" "),
          class: "fe-line",
        }),
      ),
    );
  plot.append(svg);
  profile.bands.forEach((b) => {
    const band = el(doc, "div", "fe-band");
    band.style.left = percent(b.startCm, axis);
    band.style.width = percent(b.endCm - b.startCm, axis);
    band.dataset.tip = `${b.startCm}–${b.endCm}: ${b.value}`;
    band.dataset.start = String(b.startCm);
    band.dataset.end = String(b.endCm);
    if (((b.endCm - b.startCm) / axis) * 100 > LABEL_MIN_PERCENT)
      band.textContent = b.value;
    plot.append(band);
  });
  grid.append(ylab, plot);
  node.append(grid);

  const foot = el(doc, "div", "fe-ticks");
  foot.append(el(doc, "span", undefined, "0"));
  foot.append(el(doc, "span", undefined, `${axis} cm`));
  node.append(foot);
  const hint = profileHint(profile);
  if (hint) node.append(el(doc, "div", "fe-hint", hint));
  if (!withPoints) return node;

  const points = el(doc, "div", "fe-points");
  let band = 0;
  tag.sections.forEach((s, i) => {
    const pt = el(doc, "span", "fe-pt");
    pt.dataset.index = String(i);
    const b = isProfilePoint(s) ? null : profile.bands[band++];
    pt.append(
      el(doc, "b", undefined, b ? `${b.startCm}–${b.endCm}` : s.startCm),
      ` ${s.value}`,
    );
    points.append(pt);
  });
  node.append(points);
  return node;
}

/** `raw` expand next to one LR or slot key. */
export function renderRawInline(doc, tag) {
  const details = el(doc, "details", "fe-raw");
  details.append(el(doc, "summary", undefined, "raw"));
  details.append(
    el(doc, "pre", "fe-rawtext", `${tag.rawKey}=${valueText(tag.value)}`),
  );
  return details;
}

/** `raw (n)` expand under a view: the tags it shows, one `key=value` per line. */
export function renderViewRaw(doc, tags) {
  const details = el(doc, "details", "fe-raw");
  details.append(el(doc, "summary", undefined, `raw (${tags.length})`));
  details.append(
    el(
      doc,
      "pre",
      "fe-rawtext",
      tags.map((t) => `${t.rawKey}=${valueText(t.value)}`).join("\n"),
    ),
  );
  return details;
}

/** Every property as `key=value` in the original order, read-only, copyable. */
export function renderRawBox(doc, properties) {
  const entries = Object.entries(properties).filter(([k]) => k !== "geometry");
  const lines = entries.map(([k, v]) => `${k}=${valueText(v)}`).join("\n");
  const details = el(doc, "details", "fe-rawbox");
  details.append(el(doc, "summary", undefined, `raw (${entries.length})`));
  const area = el(doc, "textarea", "fe-rawtext", lines);
  area.readOnly = true;
  area.setAttribute("readonly", "");
  area.setAttribute("rows", String(Math.min(14, Math.max(entries.length, 1))));
  details.append(area);
  details.append(copyButton(doc, lines, "copy all"));
  return details;
}

export function copyButton(doc, text, label = "copy") {
  const button = el(doc, "button", "fe-copy", label);
  button.type = "button";
  button.dataset.copy = text;
  return button;
}
